import { useState, useEffect, useContext, useMemo } from "react";
import { fetchArticles } from "../services/ArticleService.js";
import { ThemeContext } from "../ThemeContext";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [locale, setLocale] = useState("en");
  const [globalFilter, setGlobalFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const theme = useContext(ThemeContext);
  const navigate = useNavigate();

  useEffect(() => {
    loadArticles();
  }, [locale]);

  async function loadArticles() {
    const data = await fetchArticles(locale);
    setArticles(data);
  }

  // Filtered articles by country
  const filteredArticles = useMemo(() => {
    return articles
      .filter((a) => !countryFilter || a.country?.name === countryFilter)
      .filter(
        (a) =>
          globalFilter === "" ||
          a.title.toLowerCase().includes(globalFilter.toLowerCase())
      );
  }, [articles, countryFilter, globalFilter]);

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, i) => i + 1,
        id: "no",
        header: "No.",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "title",
        header: "Title",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => row.country?.name || "—",
        id: "country",
        header: "Country",
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: "publishedAt",
        header: "Published",
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredArticles,
    columns,
    state: { globalFilter, sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
  });

  const headerBg = theme.darkMode ? "#374151" : "#f3f4f6";
  const borderColor = theme.darkMode ? "#4b5563" : "#ccc";
  const rowBg = theme.darkMode ? "#1f2937" : "white";
  const rowHoverBg = theme.darkMode ? "#111827" : "#f9fafb";

  const getSortIndicator = (column) => {
    if (!column.getIsSorted()) return "⇅";
    if (column.getIsSorted() === "asc") return "⬆️";
    return "⬇️";
  };

  // Unique countries for dropdown
  const countries = useMemo(() => {
    return [...new Set(articles.map((a) => a.country?.name).filter(Boolean))];
  }, [articles]);

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ color: theme.text, marginBottom: 20 }}>Articles</h2>

      {/* Top Controls: Language Switch + Search + Country Filter */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        {/* Search + Country Filter */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flex: "1 1 auto" }}>
          <input
            type="text"
            placeholder="Search..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            style={{
              padding: 10,
              borderRadius: 5,
              border: `1px solid ${theme.darkMode ? "#555" : "#ccc"}`,
              background: theme.cardBackground,
              color: theme.text,
              flex: 1,
            }}
          />

          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            style={{
              padding: 10,
              borderRadius: 5,
              border: `1px solid ${theme.darkMode ? "#555" : "#ccc"}`,
              background: theme.cardBackground,
              color: theme.text,
              cursor: "pointer",
            }}
          >
            <option value="">All Countries</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          {/* Language Switch Button */}
          <button
            onClick={() => setLocale(locale === "en" ? "zh-CN" : "en")}
            style={{
              padding: "8px 12px",
              borderRadius: 5,
              border: "none",
              cursor: "pointer",
              backgroundColor: theme.primary,
              color: theme.buttonText,
              fontWeight: "bold",
              minWidth: 150,
            }}
          >
            {locale === "en" ? "Switch to Chinese" : "Switch to English"}
          </button>
        </div>
      </div>

      {filteredArticles.length === 0 ? (
        <p style={{ color: theme.text }}>No articles found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              minWidth: 600,
              borderCollapse: "collapse",
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} style={{ background: headerBg }}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        padding: 10,
                        borderBottom: `1px solid ${borderColor}`,
                        color: theme.text,
                        textAlign: "left",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}{" "}
                      {getSortIndicator(header.column)}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  style={{
                    background: rowBg,
                    cursor: "pointer",
                    color: theme.text,
                  }}
                  onClick={() =>
                    window.open(`/articles/${row.original.documentId}?locale=${locale}`, "_blank")
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = rowHoverBg)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = rowBg)
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: 10,
                        borderBottom: `1px solid ${borderColor}`,
                        color: theme.text,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
